import { useState, useCallback } from 'react'
import { GOTO_PROFILE_HISTORY_KEY } from '@/constants/storage'

const MAX_HISTORY = 5

function getStoredIdentifiers(): string[] {
    try {
        const stored = localStorage.getItem(GOTO_PROFILE_HISTORY_KEY)
        if (!stored) return []
        const parsed: unknown = JSON.parse(stored)
        return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
    } catch {
        return []
    }
}

function persistIdentifiers(identifiers: string[]): void {
    try {
        localStorage.setItem(GOTO_PROFILE_HISTORY_KEY, JSON.stringify(identifiers))
    } catch {
        // localStorage unavailable — silently skip
    }
}

export interface UseGotoProfileHistoryResult {
    /** Recently navigated-to identifiers (most recent first). */
    recentIdentifiers: string[]
    /** Prepend an identifier to the history (deduplicates by case-insensitive match). */
    addIdentifier: (identifier: string) => void
    /** Remove an identifier from the history. */
    removeIdentifier: (identifier: string) => void
}

export function useGotoProfileHistory(): UseGotoProfileHistoryResult {
    const [identifiers, setIdentifiers] = useState<string[]>(() => getStoredIdentifiers())

    const addIdentifier = useCallback((identifier: string) => {
        const trimmed = identifier.trim()
        if (!trimmed) return
        setIdentifiers(prev => {
            const deduped = prev.filter(h => h.toLowerCase() !== trimmed.toLowerCase())
            const updated = [trimmed, ...deduped].slice(0, MAX_HISTORY)
            persistIdentifiers(updated)
            return updated
        })
    }, [])

    const removeIdentifier = useCallback((identifier: string) => {
        setIdentifiers(prev => {
            const updated = prev.filter(h => h.toLowerCase() !== identifier.toLowerCase())
            persistIdentifiers(updated)
            return updated
        })
    }, [])

    return { recentIdentifiers: identifiers, addIdentifier, removeIdentifier }
}
